import railsFetch from "../railsFetch";
import type { SelectionStatePayload } from "../grids/PaginatedApplicationPrimerGrid";
import type { NestedFilterParams } from "../../domain/grid/resultLoader";
import { bulkSelectionPayload, isRefusal, type BulkRefusal } from "./bulkSelectionPayload";

// GR-3342: the events index archives inline. There is no BulkOperation record
// to poll, because that pipeline is nested under a single event and archiving
// events from the organisation index has no parent event to hang off. So this
// module owns the whole request: one POST, one report back.
//
// GR-3342 rework: the selection shape and the refusal guard come from
// bulkSelectionPayload, shared with the blocked dates delete, so both bulk
// actions put the same thing on the wire and the server has one refusal path
// rather than two.

export type BulkArchiveStatus =
  | "archived"
  | "skipped"
  // GR-3342: the endpoint declining a row on policy grounds (a landing site),
  // as distinct from "skipped", which is a benign no-op.
  | "refused"
  | "unauthorized"
  | "not_found"
  | "failed";

export interface BulkArchiveRow {
  id: number;
  title: string | null;
  status: BulkArchiveStatus;
  reason: string | null;
}

export interface BulkArchiveReport {
  counts: Record<BulkArchiveStatus, number>;
  results: BulkArchiveRow[];
}

export type BulkArchiveRefusal = BulkRefusal;

export type BulkArchiveResult = BulkArchiveReport | BulkArchiveRefusal;

export { isRefusal };

// True when the report says at least one row actually moved. The status tab
// counters are server rendered, so the reload that refreshes them hangs off
// this answer, whether the operator reads the report first or closed the
// dialog while the request was still in flight.
export const archivedAnything = (result: BulkArchiveResult): boolean =>
  !isRefusal(result) && result.counts.archived > 0;

// One generation per opening of the archive dialog. handleArchive captures
// current() before it awaits; when the response lands, a generation that is no
// longer current means the operator closed the dialog and reopened Archive in
// the meantime, so that report belongs to nobody. Kept out of the component so
// the rule is testable without mounting the grid.
export interface ArchiveRequestTracker {
  open: () => number;
  current: () => number;
  isCurrent: (generation: number) => boolean;
}

export const createArchiveRequestTracker = (): ArchiveRequestTracker => {
  let generation = 0;
  return {
    open: () => {
      generation += 1;
      return generation;
    },
    current: () => generation,
    isCurrent: (candidate) => candidate === generation,
  };
};

export const bulkArchiveEvents = async (
  url: string,
  selection: SelectionStatePayload,
  filterParams?: NestedFilterParams
): Promise<BulkArchiveResult> => {
  try {
    const response = await railsFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(bulkSelectionPayload(selection, filterParams)),
    });

    const body = await response.json().catch(() => ({}));
    if (response.ok) return body as BulkArchiveReport;

    return {
      errors: (body as BulkArchiveRefusal).errors ?? ["Could not archive the selected events. Please try again."],
    };
  } catch (error) {
    // A transport failure must still reach the operator: they selected rows and
    // pressed Archive, and silence reads as "it worked".
    console.error("[bulkArchiveEvents] Error:", error);
    return { errors: ["Could not archive the selected events. Please try again."] };
  }
};
